"use client";

import React from "react";
import { SectionContentProps } from "../../types";
import EditableText from "../../core/EditableText";
import EditableImage from "../../core/EditableImage";
import {
    PiPlus,
    PiTrash,
    PiUserCircle,
    PiLinkedinLogo,
    PiTwitterLogo,
    PiInstagramLogo,
    PiGlobe,
    PiEnvelope
} from "react-icons/pi";

interface TeamMember {
    id: string;
    name: string;
    role: string;
    bio: string;
    image: string;
    email?: string;
    linkedin?: string;
    twitter?: string;
    instagram?: string;
    website?: string;
}

export default function TeamGridCards({ content, onContentChange, isSelected }: SectionContentProps) {
    const fontFamily = content.fontFamily || "'Inter', sans-serif";

    const members: TeamMember[] = content.members || [
        {
            id: "1",
            name: "Sarah Johnson",
            role: "CEO & Founder",
            bio: "Visionary leader with 15+ years of experience in technology and innovation.",
            image: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=400&h=400&fit=crop",
            email: "sarah@example.com",
            linkedin: "https://linkedin.com",
            twitter: "https://twitter.com"
        },
        {
            id: "2",
            name: "Michael Chen",
            role: "CTO",
            bio: "Tech enthusiast passionate about building scalable solutions.",
            image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop",
            email: "michael@example.com",
            linkedin: "https://linkedin.com"
        },
        {
            id: "3",
            name: "Emily Rodriguez",
            role: "Head of Design",
            bio: "Award-winning designer creating beautiful user experiences.",
            image: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=400&h=400&fit=crop",
            email: "emily@example.com",
            instagram: "https://instagram.com",
            website: "https://example.com"
        },
        {
            id: "4",
            name: "David Kim",
            role: "Lead Developer",
            bio: "Full-stack developer specializing in modern web technologies.",
            image: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=400&h=400&fit=crop",
            email: "david@example.com",
            github: "https://github.com",
            twitter: "https://twitter.com"
        }
    ];

    const addMember = () => {
        const newMember: TeamMember = {
            id: Date.now().toString(),
            name: "New Team Member",
            role: "Role Title",
            bio: "Brief description of this team member's expertise and experience.",
            image: "",
            email: "",
            linkedin: "",
            twitter: ""
        };
        onContentChange?.({ ...content, members: [...members, newMember] });
    };

    const removeMember = (idx: number) => {
        const newMembers = members.filter((_: any, i: number) => i !== idx);
        onContentChange?.({ ...content, members: newMembers });
    };

    const updateMember = (idx: number, field: string, value: string) => {
        const newMembers = [...members];
        newMembers[idx] = { ...newMembers[idx], [field]: value };
        onContentChange?.({ ...content, members: newMembers });
    };

    const updateSocial = (idx: number, field: string, value: string) => {
        const newMembers = [...members];
        newMembers[idx] = { ...newMembers[idx], [field]: value };
        onContentChange?.({ ...content, members: newMembers });
    };

    return (
        <div
            className={`bg-gray-50 px-8 py-20 ${isSelected ? "ring-2 ring-blue-500" : ""}`}
            style={{ fontFamily }}
        >
            <div className="mx-auto max-w-7xl">
                {/* Section Header */}
                <div className="mb-16 text-center">
                    <h2 className="mb-4 text-4xl font-bold text-gray-900 tracking-tight">
                        <EditableText
                            value={content.title || "Meet Our Team"}
                            onChange={(val) => onContentChange?.({ ...content, title: val })}
                            as="span"
                            placeholder="Section Title"
                        />
                    </h2>
                    <p className="mx-auto max-w-2xl text-lg text-gray-600">
                        <EditableText
                            value={content.subtitle || "The talented people behind our success"}
                            onChange={(val) => onContentChange?.({ ...content, subtitle: val })}
                            as="span"
                            placeholder="Section subtitle"
                        />
                    </p>
                </div>

                {/* Team Cards Grid */}
                <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
                    {members.map((member: TeamMember, idx: number) => (
                        <div
                            key={member.id || idx}
                            className="group relative flex flex-col bg-white rounded-2xl shadow-sm transition-all duration-300 hover:shadow-xl hover:-translate-y-1 overflow-hidden"
                        >
                            {isSelected && (
                                <button
                                    onClick={(e) => { e.stopPropagation(); removeMember(idx); }}
                                    className="absolute -top-3 -right-3 hidden group-hover:flex h-8 w-8 items-center justify-center rounded-full bg-red-100 text-red-500 shadow-sm z-10"
                                    title="Remove member"
                                >
                                    <PiTrash className="h-4 w-4" />
                                </button>
                            )}

                            {/* Image */}
                            <div className="relative h-64 overflow-hidden bg-gray-100">
                                <EditableImage
                                    value={member.image}
                                    onChange={(val) => updateMember(idx, "image", val)}
                                    alt={member.name}
                                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                                    width={400}
                                    height={400}
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                                {/* Social Links Overlay */}
                                <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-2 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                                    {member.email && (
                                        <a
                                            href={`mailto:${member.email}`}
                                            className="p-2 bg-white rounded-full shadow-lg hover:bg-blue-50 transition-colors"
                                            title="Email"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <PiEnvelope className="w-4 h-4 text-gray-700" />
                                        </a>
                                    )}
                                    {member.linkedin && (
                                        <a
                                            href={member.linkedin}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="p-2 bg-white rounded-full shadow-lg hover:bg-blue-50 transition-colors"
                                            title="LinkedIn"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <PiLinkedinLogo className="w-4 h-4 text-blue-700" />
                                        </a>
                                    )}
                                    {member.twitter && (
                                        <a
                                            href={member.twitter}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="p-2 bg-white rounded-full shadow-lg hover:bg-blue-50 transition-colors"
                                            title="Twitter"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <PiTwitterLogo className="w-4 h-4 text-blue-500" />
                                        </a>
                                    )}
                                    {member.instagram && (
                                        <a
                                            href={member.instagram}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="p-2 bg-white rounded-full shadow-lg hover:bg-pink-50 transition-colors"
                                            title="Instagram"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <PiInstagramLogo className="w-4 h-4 text-pink-600" />
                                        </a>
                                    )}
                                </div>
                            </div>

                            {/* Content */}
                            <div className="flex flex-col flex-1 p-6">
                                <h3 className="text-xl font-bold text-gray-900 mb-1">
                                    <EditableText
                                        value={member.name}
                                        onChange={(val) => updateMember(idx, "name", val)}
                                        as="span"
                                        placeholder="Name"
                                    />
                                </h3>
                                <p className="text-sm font-medium text-blue-600 mb-3">
                                    <EditableText
                                        value={member.role}
                                        onChange={(val) => updateMember(idx, "role", val)}
                                        as="span"
                                        placeholder="Role"
                                    />
                                </p>
                                <p className="text-sm text-gray-600 leading-relaxed flex-1">
                                    <EditableText
                                        value={member.bio}
                                        onChange={(val) => updateMember(idx, "bio", val)}
                                        as="span"
                                        placeholder="Brief bio"
                                    />
                                </p>

                                {/* Social Links Edit (when selected) */}
                                {isSelected && (
                                    <div className="mt-4 pt-4 border-t border-gray-100 space-y-2">
                                        <div className="flex items-center gap-2">
                                            <PiEnvelope className="w-4 h-4 text-gray-400" />
                                            <input
                                                type="text"
                                                value={member.email || ""}
                                                onChange={(e) => updateSocial(idx, "email", e.target.value)}
                                                placeholder="Email URL"
                                                className="flex-1 text-xs px-2 py-1 border rounded"
                                            />
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <PiLinkedinLogo className="w-4 h-4 text-gray-400" />
                                            <input
                                                type="text"
                                                value={member.linkedin || ""}
                                                onChange={(e) => updateSocial(idx, "linkedin", e.target.value)}
                                                placeholder="LinkedIn URL"
                                                className="flex-1 text-xs px-2 py-1 border rounded"
                                            />
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <PiTwitterLogo className="w-4 h-4 text-gray-400" />
                                            <input
                                                type="text"
                                                value={member.twitter || ""}
                                                onChange={(e) => updateSocial(idx, "twitter", e.target.value)}
                                                placeholder="Twitter URL"
                                                className="flex-1 text-xs px-2 py-1 border rounded"
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}

                    {/* Add Member Button */}
                    {isSelected && (
                        <button
                            onClick={addMember}
                            className="flex flex-col items-center justify-center min-h-[400px] rounded-2xl border-2 border-dashed border-gray-300 bg-white p-8 text-gray-400 hover:border-blue-400 hover:bg-blue-50 hover:text-blue-500 transition-all"
                        >
                            <PiPlus className="h-10 w-10 mb-2" />
                            <span className="text-lg font-medium">Add Team Member</span>
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
